import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import type { PresentationStatus } from '../store/registrationsSlice'
import { formatLocalDateTime } from '../utils/datetime'
import {
  apiRoleToUiRole,
  uiRoleToApiRole,
  apiPresStatusToUiStatus,
  formatFullName,
  hasGrantedMediaConsent,
} from '../utils/participantMappers'
import { Button } from './Button'
import { ConfirmationModal } from './ConfirmationModal'
import { DetailsToggleButton } from './DetailsToggleButton'
import { RoleSelect } from './RoleBadge'
import type { ParticipantRole } from '../store/registrationsSlice'
import { PresentationBadge } from './PresentationStatusBadge'
import { PresentationActionModal } from './PresentationActionModal'
import { PresentationUploadModal } from './PresentationUploadModal'
import { ParticipationReminderModal } from './ParticipationReminderModal'
import type { ReminderParticipant } from './ParticipationReminderModal'
import { AcceptedRowActions, PresentationRowActions } from './AcceptedRowActions'
import { ParticipantConversationModal } from './ParticipantConversationModal'
import { PrivateNoteModal } from './PrivateNoteModal'
import {
  canAskForPresentation,
  canSendReminder,
  canMarkAttended,
} from '../utils/presentationEligibility'
import { intersectEligibleIds } from '../utils/bulkActions'
import { BackupRegisterModal } from './BackupRegisterModal'
import { GenerateReportButton } from './GenerateReportButton'
import { TablePagination } from './TablePagination'
import { SortableColumnHeader } from './Table'
import type { AdminRegisterFormData } from './BackupRegisterModal'
import { usePagedParticipants } from '../hooks/usePagedParticipants'
import {
  useAssignRoleMutation,
  useAcceptPresentationMutation,
  useRejectPresentationMutation,
  useAskForPresentationMutation,
  useAdminUploadPresentationMutation,
  useAdminRegisterParticipantMutation,
  useBatchUpdateParticipantsMutation,
  useRecordAttendanceAdminMutation,
  type ParticipantSummary,
  type PresentationSummary,
} from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const BellIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const MailIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const XIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const UserCheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

const PRESENTATION_ELIGIBLE = new Set(['SPEAKER', 'HOST'])

function isEligible(reg: ParticipantSummary): boolean {
  return PRESENTATION_ELIGIBLE.has(reg.role ?? '')
}

function getPresStatus(reg: ParticipantSummary): PresentationStatus {
  return apiPresStatusToUiStatus(reg.presentation?.status)
}

function buildPresMeta(pres: PresentationSummary | undefined) {
  if (!pres) return undefined
  return {
    status: apiPresStatusToUiStatus(pres.status),
    fileName: pres.fileName,
    uploadedAt: pres.uploadedAt,
    lastReminderAt: pres.reminderSentAt,
    uploadedByOrganizer: pres.uploadedByOrganizer ?? false,
  }
}

// ── Expanded detail row ───────────────────────────────────────────────────────

function ExpandedRow({
  reg,
  event,
  onPresAction,
  onUpload,
  onReminder,
  onMarkAttended,
  onNote,
}: {
  reg: ParticipantSummary
  event: AppEvent
  onPresAction: (kind: PresActionKind, ids: string[]) => void
  onUpload: (participantId: string, participantName: string) => void
  onReminder: (ids: string[]) => void
  onMarkAttended: (participantId: string) => void
  onNote: (participant: ParticipantSummary) => void
}) {
  const { t } = useTranslation()
  return (
    <tr className="bg-pax-light-purple/20">
      <td colSpan={8} className="px-6 py-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_last_name')}
            </span>
            <p className="text-pax-black">{reg.lastName || '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_first_name')}
            </span>
            <p className="text-pax-black">{reg.firstName}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_email')}
            </span>
            <p className="text-pax-black">{reg.email}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_company')}
            </span>
            <p className="text-pax-black">{reg.companyName ?? '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_media')}
            </span>
            <p className="text-pax-black">
              {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
            </p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_registered_via')}
            </span>
            <p className="text-pax-black">
              {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <PresentationRowActions
            reg={reg}
            event={event}
            onPresAction={onPresAction}
            onUpload={onUpload}
            onReminder={onReminder}
            onMarkAttended={onMarkAttended}
            onNote={onNote}
          />
        </div>
      </td>
    </tr>
  )
}

// ── Modal state types ──────────────────────────────────────────────────────────

type PresActionKind = 'ask' | 'accept' | 'reject'

interface PresActionModal {
  kind: PresActionKind
  ids: string[]
}

interface PresUploadModal {
  participantId: string
  participantName: string
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  event: AppEvent
}

/** Accepted Participants management tab — role, presentation, and reminder management. */
export function AcceptedParticipantsTab({ event }: Props) {
  const { t, i18n } = useTranslation()

  const {
    participants: accepted,
    total,
    isLoading,
    isError,
    offset,
    take,
    sort,
    order,
    setOffset,
    setTake,
    toggleSort,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'ACCEPTED',
    defaultSort: 'acceptedAt',
    defaultOrder: 'asc',
  })

  const [assignRole] = useAssignRoleMutation()
  const [acceptPresentation] = useAcceptPresentationMutation()
  const [rejectPresentation] = useRejectPresentationMutation()
  const [askForPresentation] = useAskForPresentationMutation()
  const [adminUploadPresentation] = useAdminUploadPresentationMutation()
  const [adminRegisterParticipant] = useAdminRegisterParticipantMutation()
  const [batchUpdate] = useBatchUpdateParticipantsMutation()
  const [recordAttendanceAdmin] = useRecordAttendanceAdminMutation()

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null)
  const [presActionModal, setPresActionModal] = useState<PresActionModal | null>(null)
  const [presUploadModal, setPresUploadModal] = useState<PresUploadModal | null>(null)
  const [participationReminderIds, setParticipationReminderIds] = useState<string[] | null>(null)
  const [showBackupModal, setShowBackupModal] = useState(false)
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    id: string
    name: string
    role: ParticipantRole
  } | null>(null)
  const [conversationFor, setConversationFor] = useState<{ id: string; name: string } | null>(null)
  const [noteModalFor, setNoteModalFor] = useState<ParticipantSummary | null>(null)

  function openConversation(id: string) {
    const reg = accepted.find((r) => r.id === id)
    setConversationFor({
      id,
      name: reg ? formatFullName(reg.firstName, reg.lastName) : '',
    })
  }

  const selectedCount = selectedKeys.size

  // ── Selection ────────────────────────────────────────────────────────────────

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(accepted.map((r) => r.id!)) : new Set())
  }

  function toggleExpand(id: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ── Role change ───────────────────────────────────────────────────────────────

  /** Opens the confirmation modal instead of applying the role immediately — aligns role
   *  changes with every other participant action (accept/reject/delete), which all confirm
   *  first and offer a "send notification" choice rather than saving on the spot with no
   *  visible feedback. */
  function requestRoleChange(id: string, name: string, role: ParticipantRole) {
    setPendingRoleChange({ id, name, role })
  }

  async function handleConfirmRoleChange(result: { notify: boolean; message: string }) {
    if (!pendingRoleChange) return
    const { id, role } = pendingRoleChange
    await assignRole({
      participantId: id,
      assignRoleBody: {
        role: uiRoleToApiRole(role) as 'AUDIENCE' | 'SPEAKER' | 'STAFF' | 'HOST',
        sendEmailNotification: result.notify,
        message: result.message || undefined,
      },
    })
    setPendingRoleChange(null)
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  async function handleConfirmDelete(result: { notify: boolean }) {
    if (!confirmDelete) return
    await batchUpdate({
      eventId: event.id,
      batchBody: {
        participantIds: confirmDelete,
        action: 'DELETE',
        sendEmailNotification: result.notify,
      },
    })
    setSelectedKeys(new Set())
    setConfirmDelete(null)
  }

  // ── Presentation actions ───────────────────────────────────────────────────────

  function openPresAction(kind: PresActionKind, ids: string[]) {
    setPresActionModal({ kind, ids })
  }

  async function handlePresActionConfirm({
    notify,
    message,
  }: {
    notify: boolean
    message?: string
  }) {
    if (!presActionModal) return
    const { kind, ids } = presActionModal

    if (kind === 'ask') {
      if (ids.length === 1) {
        await askForPresentation({
          participantId: ids[0],
          presentationRemindBody: { customMessage: message },
        })
      } else {
        await batchUpdate({
          eventId: event.id,
          batchBody: {
            participantIds: ids,
            action: 'ASK_FOR_PRESENTATION',
            sendEmailNotification: true,
            presentationMessage: message,
          },
        })
      }
    } else if (kind === 'accept') {
      if (ids.length === 1) {
        await acceptPresentation({
          participantId: ids[0],
          presentationActionBody: { sendEmailNotification: notify },
        })
      } else {
        await batchUpdate({
          eventId: event.id,
          batchBody: {
            participantIds: ids,
            action: 'ACCEPT_PRESENTATION',
            sendEmailNotification: notify,
          },
        })
      }
    } else if (kind === 'reject') {
      if (ids.length === 1) {
        await rejectPresentation({
          participantId: ids[0],
          presentationRejectBody: { sendEmailNotification: notify, rejectionMessage: message },
        })
      } else {
        await batchUpdate({
          eventId: event.id,
          batchBody: {
            participantIds: ids,
            action: 'REJECT_PRESENTATION',
            sendEmailNotification: notify,
            presentationMessage: message,
          },
        })
      }
    }

    setPresActionModal(null)
  }

  // ── Presentation upload ────────────────────────────────────────────────────────

  async function handlePresentationUpload(participantId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const result = await adminUploadPresentation({
      participantId,
      body: formData as unknown as { file: Blob },
    })
    if ('error' in result) throw result.error
  }

  // ── Participation reminder ────────────────────────────────────────────────────

  async function handleReminderDone(messages: Record<string, string>) {
    if (!participationReminderIds) return
    await batchUpdate({
      eventId: event.id,
      batchBody: {
        participantIds: participationReminderIds,
        action: 'SEND_PARTICIPATION_REMINDER',
        sendEmailNotification: true,
        reminderMessages: messages,
      },
    })
    setParticipationReminderIds(null)
  }

  // ── Admin register ────────────────────────────────────────────────────────────

  async function handleAdminRegister(data: AdminRegisterFormData) {
    await adminRegisterParticipant({
      eventId: event.id,
      adminRegisterBody: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        companyName: data.companyName,
        consentMediaRecording: data.consentMediaRecording,
        organizerConsentConfirmed: true,
        sendEmailNotification: true,
        role: uiRoleToApiRole(data.role) as 'AUDIENCE' | 'SPEAKER' | 'STAFF' | 'HOST',
        language: data.language,
      },
    })
  }

  // ── Manual attendance ──────────────────────────────────────────────────────────

  /** Lets an organizer mark a participant as attended without them scanning their ticket QR
   *  code at the check-in kiosk — reuses the same idempotent kiosk endpoint. */
  async function handleMarkAttended(participantId: string) {
    await recordAttendanceAdmin({ eventId: event.id, participantId })
  }

  /** Bulk version of {@link handleMarkAttended} — fires the same admin check-in call for every
   *  id in parallel, then clears the selection once all requests have settled. */
  async function handleBulkMarkAttended(ids: string[]) {
    await Promise.all(
      ids.map((participantId) => recordAttendanceAdmin({ eventId: event.id, participantId }))
    )
    setSelectedKeys(new Set())
  }

  // ── Bulk action eligibility ────────────────────────────────────────────────────
  //
  // Every bulk action is computed as the intersection of each selected row's individual
  // eligibility for that action (see `intersectEligibleIds`) — it only shows once *every*
  // selected row qualifies (e.g. mixing in a row with an already-accepted presentation, an
  // ineligible role, or an already-attended participant hides the corresponding action entirely)
  // — never applied to a partial subset of the selection, since that would silently skip rows.

  const selectedRegs = accepted.filter((r) => selectedKeys.has(r.id!))
  const getRegId = (r: ParticipantSummary) => r.id!
  const bulkAskIds = intersectEligibleIds(selectedRegs, getRegId, canAskForPresentation)
  const bulkAcceptIds = intersectEligibleIds(
    selectedRegs,
    getRegId,
    (r) => isEligible(r) && getPresStatus(r) === 'uploaded'
  )
  const bulkRejectIds = bulkAcceptIds
  const bulkReminderIds = intersectEligibleIds(selectedRegs, getRegId, canSendReminder)
  const bulkMarkAttendedIds = intersectEligibleIds(selectedRegs, getRegId, canMarkAttended)

  // ── Modal config ──────────────────────────────────────────────────────────────

  function getPresActionModalConfig(modal: PresActionModal) {
    const { kind, ids } = modal
    const isSingle = ids.length === 1
    if (kind === 'ask')
      return {
        title: isSingle
          ? t('pres_ask_title_single')
          : t('pres_ask_title_bulk', { count: ids.length }),
        confirmLabel: t('pres_ask_confirm'),
        showNotify: false,
        showMessage: true,
        messageLabel: t('pres_ask_message_label'),
        messagePlaceholder: t('pres_message_placeholder'),
      }
    if (kind === 'accept')
      return {
        title: isSingle
          ? t('pres_accept_title_single')
          : t('pres_accept_title_bulk', { count: ids.length }),
        confirmLabel: t('pres_accept_confirm'),
        showNotify: true,
        showMessage: false,
      }
    return {
      title: isSingle
        ? t('pres_reject_title_single')
        : t('pres_reject_title_bulk', { count: ids.length }),
      confirmLabel: t('pres_reject_confirm'),
      showNotify: true,
      showMessage: isSingle,
      messageLabel: t('pres_message_label'),
      messagePlaceholder: t('pres_message_placeholder'),
    }
  }

  // ── Table columns ─────────────────────────────────────────────────────────────

  const columns = [
    {
      // Name, not email — the email address is available in the expandable Details section.
      header: t('reg_col_name'),
      sortKey: 'lastName',
      render: (row: ParticipantSummary) => (
        <div>
          <p className="text-pax-black font-medium">
            {row.lastName} {row.firstName}
          </p>
          <p className="text-pax-gray text-xs tabular-nums">
            {row.acceptedAt ? formatLocalDateTime(row.acceptedAt, i18n.language) : '—'}
          </p>
        </div>
      ),
    },
    {
      header: t('accept_role_label'),
      render: (row: ParticipantSummary) => (
        <RoleSelect
          role={apiRoleToUiRole(row.role)}
          registrationId={row.id!}
          onChange={(id, role) =>
            requestRoleChange(id, formatFullName(row.firstName, row.lastName), role)
          }
        />
      ),
    },
    {
      header: t('acc_col_presentation'),
      render: (row: ParticipantSummary) => {
        if (!isEligible(row)) return <span className="text-pax-gray text-sm">—</span>
        return <PresentationBadge registrationId={row.id!} meta={buildPresMeta(row.presentation)} />
      },
    },
    {
      header: t('acc_col_last_reminder'),
      render: (row: ParticipantSummary) =>
        row.lastParticipationReminderSentAt ? (
          <span className="text-pax-black text-xs tabular-nums">
            {formatLocalDateTime(row.lastParticipationReminderSentAt, i18n.language)}
          </span>
        ) : (
          <span className="text-pax-gray text-xs">{t('acc_last_reminder_never')}</span>
        ),
    },
    {
      // Header left visually blank (#274) — the loop below adds an aria-label instead.
      header: '',
      render: (row: ParticipantSummary) => (
        // Right-align the icon strip so it stays flush with the column's right edge no matter
        // how many icons a given row shows (e.g. Speaker/Host rows also get an "Ask" icon) —
        // otherwise left-aligned rows of different lengths appear to jitter horizontally.
        <div className="flex justify-end">
          <AcceptedRowActions
            reg={row}
            expandedKeys={expandedKeys}
            onExpand={toggleExpand}
            onDelete={(id) => setConfirmDelete([id])}
            onConversation={openConversation}
          />
        </div>
      ),
    },
  ]

  // Build flat row list
  const flatRows: React.ReactElement[] = []
  for (const reg of accepted) {
    const isSelected = selectedKeys.has(reg.id!)
    const isExpanded = expandedKeys.has(reg.id!)
    const rowCls = [
      'border-pax-purple/10 border-b transition-colors last:border-0',
      isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
    ].join(' ')

    flatRows.push(
      <tr key={reg.id} className={rowCls}>
        <td className="px-4 py-3">
          <input
            type="checkbox"
            aria-label={t('table_select_row', { name: reg.email })}
            checked={isSelected}
            onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
            className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
          />
        </td>
        {columns.map((col, ci) => (
          <td
            key={ci}
            className={`text-pax-black text-sm ${ci === columns.length - 1 ? 'px-2 py-2' : 'px-4 py-3'}`}
          >
            {col.render(reg)}
          </td>
        ))}
      </tr>
    )

    if (isExpanded) {
      flatRows.push(
        <ExpandedRow
          key={`${reg.id!}-exp`}
          reg={reg}
          event={event}
          onPresAction={openPresAction}
          onUpload={(pid, name) =>
            setPresUploadModal({ participantId: pid, participantName: name })
          }
          onReminder={(ids) => setParticipationReminderIds(ids)}
          onMarkAttended={(id) => void handleMarkAttended(id)}
          onNote={(p) => setNoteModalFor(p)}
        />
      )
    }
  }

  const allSelected = accepted.length > 0 && selectedKeys.size === accepted.length
  const someSelected = selectedKeys.size > 0 && !allSelected

  const reminderRegs: ReminderParticipant[] = participationReminderIds
    ? accepted
        .filter((r) => participationReminderIds.includes(r.id!))
        .map((r) => ({
          id: r.id!,
          firstName: r.firstName ?? '',
          lastName: r.lastName,
          email: r.email ?? '',
        }))
    : []

  // ── Loading / error states ─────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
        <div className="bg-pax-purple/10 mx-auto h-4 w-32 rounded" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="border-pax-red/20 bg-pax-red/5 rounded-2xl border p-6 text-center text-sm text-red-500">
        {t('acc_load_error')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Combined pager + table actions. Bulk actions sit after the pager on the left when
          rows are selected; report / backup-register stay on the right. */}
      <TablePagination
        total={total}
        offset={offset}
        take={take}
        onOffsetChange={setOffset}
        onTakeChange={setTake}
        toolbarTestId="accepted-toolbar"
        selectionActions={
          selectedCount > 0 ? (
            <>
              <span className="text-pax-gray text-xs font-medium">
                {t('n_selected', { count: selectedCount })}
              </span>
              {bulkAskIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<MailIcon />}
                  onClick={() => openPresAction('ask', bulkAskIds)}
                >
                  {t('pres_action_ask')}
                </Button>
              )}
              {bulkAcceptIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<CheckIcon />}
                  onClick={() => openPresAction('accept', bulkAcceptIds)}
                >
                  {t('pres_action_accept')}
                </Button>
              )}
              {bulkRejectIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<XIcon />}
                  onClick={() => openPresAction('reject', bulkRejectIds)}
                >
                  {t('pres_action_reject')}
                </Button>
              )}
              {bulkReminderIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<BellIcon />}
                  onClick={() => setParticipationReminderIds(bulkReminderIds)}
                >
                  {t('reminder_action_send')}
                </Button>
              )}
              {bulkMarkAttendedIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<UserCheckIcon />}
                  onClick={() => void handleBulkMarkAttended(bulkMarkAttendedIds)}
                >
                  {t('acc_mark_attended')}
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => setConfirmDelete(Array.from(selectedKeys))}
              >
                {t('reg_delete')}
              </Button>
            </>
          ) : undefined
        }
        actions={
          <>
            <GenerateReportButton
              eventId={event.id!}
              reportType="accepted"
              labelKey="report_generate_accepted_pdf"
            />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<PlusIcon />}
              onClick={() => setShowBackupModal(true)}
            >
              {t('reg_backup_btn')}
            </Button>
          </>
        }
      >
        <div data-testid="mobile-view" className="flex flex-col gap-3 md:hidden">
          {accepted.length === 0 ? (
            <p className="text-pax-gray py-6 text-center text-sm">{t('acc_empty')}</p>
          ) : (
            accepted.map((reg) => {
              const isSelected = selectedKeys.has(reg.id!)
              return (
                <div
                  key={reg.id}
                  className={[
                    'border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm',
                    isSelected ? 'bg-pax-light-blue/40' : '',
                  ].join(' ')}
                >
                  <div className="mb-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={t('table_select_row', { name: reg.email })}
                      checked={isSelected}
                      onChange={(e) => handleSelectRow(reg.id!, e.target.checked)}
                      className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-pax-black font-medium">
                        {reg.lastName} {reg.firstName}
                      </p>
                      <p className="text-pax-gray text-xs">{reg.email}</p>
                      {reg.companyName && (
                        <p className="text-pax-gray text-xs">{reg.companyName}</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {reg.attendedAt && (
                          <span className="bg-pax-green/15 text-pax-green rounded-full px-2 py-0.5 text-[10px] font-semibold">
                            {t('acc_attended_badge')}
                          </span>
                        )}
                        {isEligible(reg) && (
                          <PresentationBadge
                            registrationId={reg.id!}
                            meta={buildPresMeta(reg.presentation)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                    <RoleSelect
                      role={apiRoleToUiRole(reg.role)}
                      registrationId={reg.id!}
                      onChange={(id, role) =>
                        requestRoleChange(id, formatFullName(reg.firstName, reg.lastName), role)
                      }
                    />
                    {/* Collapsed: no icon strip — Details opens the panel. Expanded: collapse + delete only. */}
                    {expandedKeys.has(reg.id!) ? (
                      <AcceptedRowActions
                        reg={reg}
                        expandedKeys={expandedKeys}
                        onExpand={toggleExpand}
                        onDelete={(id) => setConfirmDelete([id])}
                        onConversation={openConversation}
                        small
                      />
                    ) : (
                      <DetailsToggleButton expanded={false} onClick={() => toggleExpand(reg.id!)} />
                    )}
                  </div>
                  {expandedKeys.has(reg.id!) && (
                    <div className="border-pax-purple/20 mt-3 rounded-xl border bg-slate-50 p-3 text-xs">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        <div>
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_last_name')}
                          </span>
                          <p className="text-pax-black">{reg.lastName || '—'}</p>
                        </div>
                        <div>
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_first_name')}
                          </span>
                          <p className="text-pax-black">{reg.firstName}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_email')}
                          </span>
                          <p className="text-pax-black break-all">{reg.email}</p>
                        </div>
                        <div>
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_company')}
                          </span>
                          <p className="text-pax-black">{reg.companyName ?? '—'}</p>
                        </div>
                        <div>
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_media')}
                          </span>
                          <p className="text-pax-black">
                            {hasGrantedMediaConsent(reg) ? t('reg_media_yes') : t('reg_media_no')}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-pax-gray text-[10px] font-semibold uppercase">
                            {t('reg_field_registered_via')}
                          </span>
                          <p className="text-pax-black">
                            {reg.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <PresentationRowActions
                          reg={reg}
                          event={event}
                          onPresAction={openPresAction}
                          onUpload={(pid, name) =>
                            setPresUploadModal({ participantId: pid, participantName: name })
                          }
                          onReminder={(ids) => setParticipationReminderIds(ids)}
                          onMarkAttended={(id) => void handleMarkAttended(id)}
                          onNote={(p) => setNoteModalFor(p)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div data-testid="desktop-view" className="hidden md:block">
          <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="overflow-x-auto">
              {/* `table-fixed` + percentage column widths (summing to 100%) keep every column
                stable regardless of how much content a cell renders (e.g. the Actions cell
                shows more icons for Speaker/Host than Audience) — without this the whole table
                reflows on role change. Percentages (rather than fixed px widths) always fit the
                container, so the table never overflows/horizontal-scrolls on typical desktop
                widths. */}
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col className="w-[4%]" />
                  <col className="w-[28%]" />
                  <col className="w-[14%]" />
                  <col className="w-[16%]" />
                  <col className="w-[18%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead>
                  <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        aria-label={t('select_all')}
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                      />
                    </th>
                    {columns.map((col, i) => {
                      // Actions is always the last column — its header stays visually blank
                      // (#274) but keeps an aria-label so the column still has a real name.
                      const isActionsCol = i === columns.length - 1
                      return (
                        <th
                          key={i}
                          aria-label={isActionsCol ? t('reg_col_actions') : undefined}
                          className={`text-pax-gray px-4 py-3 text-xs font-semibold tracking-wider whitespace-nowrap ${
                            isActionsCol ? 'text-right' : 'text-left'
                          }`}
                        >
                          {col.sortKey ? (
                            <SortableColumnHeader
                              label={col.header}
                              field={col.sortKey}
                              activeField={sort}
                              order={order}
                              onSort={toggleSort}
                            />
                          ) : (
                            col.header
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {accepted.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-pax-gray px-4 py-10 text-center text-sm">
                        {t('acc_empty')}
                      </td>
                    </tr>
                  ) : (
                    flatRows
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </TablePagination>

      {/* Admin register (backup) modal */}
      {showBackupModal && (
        <BackupRegisterModal
          event={event}
          onClose={() => setShowBackupModal(false)}
          onSubmit={handleAdminRegister}
        />
      )}

      {/* Confirm delete */}
      {confirmDelete !== null && (
        <ConfirmationModal
          title={t('reg_delete_confirm', { count: confirmDelete.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={(r) => void handleConfirmDelete(r)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Confirm role change */}
      {pendingRoleChange !== null && (
        <PresentationActionModal
          title={t('acc_role_change_confirm_title', {
            name: pendingRoleChange.name,
            role: t(`role_${pendingRoleChange.role.toLowerCase()}`),
          })}
          confirmLabel={t('btn_confirm')}
          showNotify={true}
          showMessage={true}
          messageLabel={t('pres_ask_message_label')}
          messagePlaceholder={t('pres_message_placeholder')}
          onConfirm={(r) => void handleConfirmRoleChange(r)}
          onCancel={() => setPendingRoleChange(null)}
        />
      )}

      {/* Presentation action modal */}
      {presActionModal !== null &&
        (() => {
          const cfg = getPresActionModalConfig(presActionModal)
          return (
            <PresentationActionModal
              {...cfg}
              onConfirm={(r) => void handlePresActionConfirm(r)}
              onCancel={() => setPresActionModal(null)}
            />
          )
        })()}

      {/* Presentation upload modal */}
      {presUploadModal !== null && (
        <PresentationUploadModal
          registrationId={presUploadModal.participantId}
          participantName={presUploadModal.participantName}
          onUpload={handlePresentationUpload}
          onDone={() => setPresUploadModal(null)}
          onCancel={() => setPresUploadModal(null)}
        />
      )}

      {/* Participation reminder modal */}
      {participationReminderIds !== null && reminderRegs.length > 0 && (
        <ParticipationReminderModal
          registrations={reminderRegs}
          event={event}
          onDone={(messages) => void handleReminderDone(messages)}
          onCancel={() => setParticipationReminderIds(null)}
        />
      )}

      {/* Private note modal */}
      {noteModalFor && (
        <PrivateNoteModal
          participant={noteModalFor}
          onDone={() => setNoteModalFor(null)}
          onCancel={() => setNoteModalFor(null)}
        />
      )}

      {/* Conversation history modal */}
      {conversationFor && (
        <ParticipantConversationModal
          participantId={conversationFor.id}
          participantName={conversationFor.name}
          onClose={() => setConversationFor(null)}
          actions={
            <Button
              variant="danger"
              size="sm"
              leftIcon={<TrashIcon />}
              onClick={() => {
                const id = conversationFor.id
                setConversationFor(null)
                setConfirmDelete([id])
              }}
            >
              {t('reg_delete')}
            </Button>
          }
        />
      )}
    </div>
  )
}
